import React from 'react';
import CodeBlock from '@/components/CodeBlock/CodeBlock';
import { KeyFacts } from '../../components/LessonUI';

const semanticNavigationCode = `// Precise navigation using Element-based properties

const activeItem = document.querySelector('.nav-item.active');

// 1. Moving Sideways (Siblings)
const nextItem = activeItem.nextElementSibling;
const prevItem = activeItem.previousElementSibling;

// 2. Moving Upwards (Ancestors)
const navList = activeItem.parentElement;
// Finding the specific ancestor by selector
const navWrapper = activeItem.closest('nav.main-navigation');

// 3. Moving Downwards (Children)
const subMenu = activeItem.querySelector('.dropdown-menu'); // Scoped search
const firstSubItem = activeItem.firstElementChild;`;

const treeWalkerCode = `// Industrial-grade Tree Navigation: The TreeWalker API

// Filtering the tree to only visit specific node archetypes
const walker = document.createTreeWalker(
    document.body,
    NodeFilter.SHOW_TEXT, // Only interested in TextNodes
    {
        acceptNode(node) {
            // Logic to include/exclude nodes dynamically
            return node.nodeValue.trim().length &gt; 0 
                ? NodeFilter.FILTER_ACCEPT 
                : NodeFilter.FILTER_SKIP;
        }
    }
);

let node;
while (node = walker.nextNode()) {
    console.log("Processing text node:", node.nodeValue);
}

// TreeWalker is more memory-efficient than recursive functions 
// because it doesn't build up a massive function call stack.`;

const closestAlgorithmCode = `// The Anatomy of "closest()"

// This method traverses up the tree manually (if polyfilled):
function polyfillClosest(element, selector) {
    let current = element;
    while (current && current.nodeType === 1) {
        if (current.matches(selector)) return current;
        current = current.parentElement;
    }
    return null;
}

// Practical usage in Event Delegation
document.addEventListener('click', (e) => {
    // Determine which logical component was clicked, 
    // even if the user clicked on a nested \u003cspan\u003e or \u003ci\u003e
    const card = e.target.closest('.user-card');
    if (card) {
        console.log("Card ID:", card.dataset.id);
    }
});`;

const containmentCode = `// Logic for Boundary Detection and Containment

const modal = document.querySelector('.modal-content');
const backdrop = document.querySelector('.modal-backdrop');

// Checking if a node exists inside another's subtree
document.addEventListener('mousedown', (e) => {
    if (modal && !modal.contains(e.target)) {
        console.log("Click detected outside the modal!");
        closeModal();
    }
});

// The .matches() check for specific logic
if (e.target.matches('button:not(.disabled)')) {
    performAction();
}`;

const JSDOMTraversal = () => {
    return (
        <div className="premium-course-content">
            <div className="content-hero">
                <div className="hero-badge">DOM Manipulation</div>
                <h1>Advanced Tree Traversal</h1>
                <p className="hero-lead">
                    Navigate the DOM tree with architectural precision. Master the
                    distinction between Node-based and Element-based traversal,
                    harness the power of the `closest` algorithm, and implement
                    high-performance tree walking using the `TreeWalker` API.
                </p>
            </div>

            <div className="content-section">
                <h2>The Traversal Strategy: Nodes vs. Elements</h2>
                <p>
                    Navigating the DOM is essentially a graph traversal problem where each element
                    is a node and their relationships (parent, child, sibling) are the edges.
                    Architecturally, we must distinguish between the "Raw" Node Map and the
                    "Symptomatic" Element Tree. The Node tree includes every structural artifact,
                    such as whitespace TextNodes and comments, which can lead to brittle
                    code if accessed via properties like `nextSibling`. For production-grade
                    UI logic, we almost exclusively utilize the **Element-based APIs**
                    (`nextElementSibling`, `children`, etc.). These modern properties act as a
                    built-in filter, ensuring that our scripts only interact with meaningful
                    HTML elements, thereby making our traversal logic resilient to changes
                    in code formatting or indentation.
                </p>
                <p>
                    When choosing a traversal strategy, consider the "direction" of your
                    logic. Downward traversal is most efficient when scopedinstead of
                    searching the whole document, search only the children of the current
                    node. Upward traversal is typically used for state resolution or event
                    delegation, where you need to find the "context" of a click. Side-to-side
                    traversal (siblings) is useful for sequential UI components like tabs
                    or wizard steps. By mastering these three axes of movement, you gain
                    the ability to build dynamic components that understand their own
                    position within the document without relying on hardcoded IDs or
                    redundant queries. This context-awareness is what separates a modern
                    modular component from a legacy monolithic script.
                </p>

                <CodeBlock code={semanticNavigationCode} language="javascript" />
            </div>

            <div className="content-section">
                <h2>The "Closest" Algorithm: Contextual Discovery</h2>
                <p>
                    The introduction of the `closest()` method solved one of the most frustrating
                    challenges in front-end development: finding the "meaningful" ancestor of a
                    deeply nested element. In a complex UI, a user might click on a <code>&lt;span&gt;</code> or
                    an <code>&lt;i&gt;</code> icon inside a <code>&lt;button&gt;</code>. Without `closest()`, developers had to
                    write manual `while` loops to climb the tree and check if the parent was
                    the actual button they were looking for. The `closest()` method automates this,
                    climbing up the DOM tree and returning the first ancestor that matches a specific
                    CSS selector. This method is the backbone of modern event delegation
                    architectures, allowing you to handle thousands of clicks with a single
                    event listener while maintaining perfectly accurate target identification.
                </p>
                <p>
                    From a performance standpoint, `closest()` is highly optimized, but it
                    still represents an O(n) operation where 'n' is the depth of the tree.
                    In extremely deep documents (like large generated spreadsheets or data
                    visualizations), excessive use of `closest()` inside high-frequency
                    events (like `mousemove`) can lead to measurable frame drops. To optimize
                    this, senior engineers often "pre-calculate" parent relationships or
                    use a bounded traversal strategy that stops climbing after a certain
                    number of levels. Additionally, remember that `closest()` begins its
                    search at the element itself; if the current element matches the
                    selector, it is returned immediately. This self-inclusive check is
                    a vital detail for building robust interactive behaviors that work
                    regardless of whether the user clicked the padding or the inner text.
                </p>

                <CodeBlock code={closestAlgorithmCode} language="javascript" />
            </div>

            <div className="content-section">
                <h2>The TreeWalker API: Industrial Tree Searching</h2>
                <p>
                    For scenarios where you need to process every node in a large documentsuch
                    as text-highlighting search results or extracting metadatastandard
                    recursion should be avoided. Recursive functions in JavaScript are
                    limited by the size of the Call Stack; a deep enough DOM tree can trigger
                    a "RangeError: Maximum call stack size exceeded." To solve this, browsers
                    provide the `TreeWalker` API. The TreeWalker is an iterative state
                    machine that allows you to "step" through the tree without adding frames
                    to the stack. It is exponentially more memory-efficient than recursion
                    and provides built-in filtering capabilities, allowing you to tell the
                    engine to only "show" specific types of nodes (e.g., only elements or
                    only text nodes) while skipping everything else.
                </p>
                <p>
                    The TreeWalker also respects the "Filter" pattern, where you can provide a
                    custom function to decide whether a node should be accepted, rejected, or
                    if its entire subtree should be skipped. This level of granular control
                    allows you to build powerful document processing tools that can handle
                    millions of nodes without freezing the UI. In modern application
                    development, TreeWalkers are frequently used in the implementation of
                    Virtual DOM algorithms, accessibility scanners, and data scraping
                    tools. Mastering this API is a clear signal of advanced expertise,
                    demonstrating an understanding of the browser's lower-level
                    architectural constraints and the tools designed to overcome them.
                </p>

                <CodeBlock code={treeWalkerCode} language="javascript" />
            </div>

            <div className="content-section">
                <h2>Boundary Detection and Containment</h2>
                <p>
                    Beyond just moving through the tree, we often need to answer questions
                    about the "containment" of elements. The `contains()` method is the
                    primary tool for this, allowing you to check if a specific node is a
                    descendant of another. This is most commonly used for "Click-Outside"
                    logica staple of modern UI design for closing modals, dropdowns, and
                    tooltips. By checking if the clicked target is "contained" within your
                    component's root, you can determine if the user is interacting with your
                    module or trying to leave it. This logic is much more robust than
                    checking class names, as `contains()` works correctly even if the
                    layout of your component changes significantly over time.
                </p>
                <p>
                    Complementing containment is the `matches()` method, which allows you to
                    programmatically check if an element would be selected by a specific
                    CSS selector. This is particularly useful in event listeners where you
                    need to differentiate between different types of targets (e.g.,
                    "Is this a primary button or a secondary one?"). By using `matches()`
                    instead of manually checking `element.className.includes()`, you
                    leverage the browser's optimized CSS selection engine, making your
                    code faster and more declarative. Together, these methods allow you
                    to define the "territory" of your components, ensuring they react
                    only to the relevant user actions while ignoring the rest of the
                    document's noise.
                </p>

                <CodeBlock code={containmentCode} language="javascript" />
            </div>

            <div className="content-section">
                <h2>Engineering Best Practices</h2>
                <p>
                    Always prioritize Element-based properties (`nextElementSibling`, `children`) over
                    Node-based ones unless you explicitly need to handle text or comments. Avoid
                    "Deep Walk-throughs" in the main thread during user scrolling; instead, use
                    `IntersectionObserver` or cache your tree references beforehand. When
                    navigating up the tree, use `closest()` instead of manual parent loops to
                    improve readability and leverage browser optimizations. Finally, for
                    any comprehensive tree processing, replace recursive logic with the
                    `TreeWalker` API to ensure memory stability and performance on lower-end
                    devices. By respecting these architectural boundaries, you build a
                    front-end that is both powerful and highly resilient.
                </p>

                <KeyFacts><ul>
                        <li>**Purity:** Prefer Element properties to avoid whitespace TextNodes.</li>
                        <li>**Context:** Use `closest()` for effortless ancestor lookup and delegation.</li>
                        <li>**Scale:** Use `TreeWalker` instead of recursion for large document processing.</li>
                        <li>**Boundaries:** Use `contains()` for robust Click-Outside and modal logic.</li>
                        <li>**Optimization:** Scoped queries are always faster than global document queries.</li>
                        <li>**Verification:** Use `matches()` to check element archetypes declaratively.</li>
                        <li>**Awareness:** Recognize that `children` is a live collection; handle with care.</li>
                    </ul></KeyFacts>
            </div>

        </div>
    );
};

export default JSDOMTraversal;


